/**
 * Papers, first on the subject page (bug fix 1, spec 5 §2): newest first, sorted here so no
 * API change or race can reorder them; the five most recent shown and the rest behind "Older
 * papers (N)", opened for the visit; a paper being written or that needs Retry always shown,
 * whatever its age. The row itself is the screen's (renderRow).
 */
import { useState, type ReactNode, type RefObject } from "react";
import type { PaperRow } from "../api/api";
import { isPaperMoving } from "./describe";

export const RECENT = 5;

const newest = (a: PaperRow, b: PaperRow) => Date.parse(b.created_at) - Date.parse(a.created_at);

/** The five newest, plus any moving or failed; the rest, newest first. */
export function splitPapers(papers: PaperRow[]): { shown: PaperRow[]; older: PaperRow[] } {
  const sorted = [...papers].sort(newest);
  const recent = new Set(sorted.slice(0, RECENT).map((p) => p.id));
  const keep = (p: PaperRow) => recent.has(p.id) || isPaperMoving(p) || p.status === "failed";
  return { shown: sorted.filter(keep), older: sorted.filter((p) => !keep(p)) };
}

type Props = {
  papers: PaperRow[] | null;
  renderRow: (p: PaperRow) => ReactNode;
  sectionRef?: RefObject<HTMLElement>;
};

export function PapersSection({ papers, renderRow, sectionRef }: Props) {
  const [olderOpen, setOlderOpen] = useState(false);
  const { shown, older } = splitPapers(papers ?? []);
  return (
    <section aria-labelledby="papers" ref={sectionRef}>
      <h2 id="papers" className="eyebrow subject__eyebrow">
        Papers
      </h2>
      {shown.length > 0 ? (
        <>
          <ul className="doc-list">{shown.map(renderRow)}</ul>
          {older.length > 0 &&
            (olderOpen ? (
              <ul className="doc-list" aria-label="Older papers">
                {older.map(renderRow)}
              </ul>
            ) : (
              <button
                type="button"
                className="button button--quiet papers__older"
                aria-expanded={false}
                onClick={() => setOlderOpen(true)}
              >
                Older papers ({older.length})
              </button>
            ))}
        </>
      ) : (
        <div className="subject__empty">
          <p className="muted">No papers yet.</p>
          <p className="panel__text papers__hint">
            Make one from the panel <span className="papers__hint--wide">beside</span>
            <span className="papers__hint--narrow">below</span> this.
          </p>
        </div>
      )}
    </section>
  );
}
